import React, { useCallback, useMemo, useState } from "react";
import Papa from "papaparse";
import { lookupDraftMasterMenuUPCs } from "@/api/admin.js";
import Badge from "@/components/Badges";
import { PrimaryButton, SecondaryButton } from "@/components/Buttons.jsx";
import FileUpload from "@/components/FileUpload.jsx";
import Loader from "@/components/Loader.jsx";
import ModalBasic from "@/components/ModalBasic.jsx";

const UPC_FIELD = "retailer_upc";
const UPC_FIELD_LABEL = "UPC / Barcode";
const MAX_LOOKUP_BATCH = 5000;

const UPC_HEADER_RE = /^(upc|barcode|ean|gtin|retailer[_\s-]?upc)$/i;

const RESULT_FILTER_ALL = "all";
const RESULT_FILTER_CATALOG = "catalog";
const RESULT_FILTER_UNMATCHED = "unmatched";
const RESULT_FILTER_NOT_FOUND = "not_found";
const RESULT_FILTER_INVALID = "invalid";

function formatMappingExample(text, maxLen = 48) {
  if (!text) return "";
  return text.length > maxLen ? `${text.slice(0, maxLen)}…` : text;
}

function guessUpcColumn(headers) {
  for (const header of headers) {
    const normalized = String(header || "")
      .trim()
      .replace(/[\s_-]+/g, "_");
    if (UPC_HEADER_RE.test(normalized)) {
      return header;
    }
  }
  return "";
}

function extractUpcsFromRows(headers, dataRows, upcColumn) {
  const colIndex = headers.indexOf(upcColumn);
  if (colIndex < 0) return [];
  const out = [];
  for (const row of dataRows) {
    if (!Array.isArray(row)) continue;
    const raw = String(row[colIndex] ?? "").trim();
    if (raw) out.push(raw);
  }
  return out;
}

const RESULT_FILTER_QUEUED = "queued";

function rowMatchesFilter(row, filter) {
  if (filter === RESULT_FILTER_ALL) return true;
  if (filter === RESULT_FILTER_INVALID) return row.invalid;
  if (filter === RESULT_FILTER_CATALOG) return row.in_catalog;
  if (filter === RESULT_FILTER_QUEUED) return row.queued_for_autocreate;
  if (filter === RESULT_FILTER_UNMATCHED) {
    return row.in_unmatched_feed && !row.in_catalog && !row.queued_for_autocreate;
  }
  if (filter === RESULT_FILTER_NOT_FOUND) {
    return !row.invalid && !row.in_catalog && !row.in_unmatched_feed;
  }
  return true;
}

function statusBadge(row) {
  if (row.invalid) {
    return <Badge color="red">Invalid UPC</Badge>;
  }
  if (row.in_catalog) {
    return <Badge color="green">In catalog</Badge>;
  }
  if (row.queued_for_autocreate) {
    return <Badge color="blue">Queued for autocreate</Badge>;
  }
  if (row.in_unmatched_feed) {
    return <Badge color="yellow">On unmatched feed</Badge>;
  }
  return <Badge color="gray">Not found</Badge>;
}

function catalogCell(row) {
  if (!row.in_catalog || !row.catalog) {
    return <span className="text-slate-400">—</span>;
  }
  const { sku_id: skuId, title, subtitle } = row.catalog;
  const label = [title, subtitle].filter(Boolean).join(" — ") || skuId || "—";
  return (
    <div className="min-w-0">
      <div className="text-slate-800 dark:text-slate-100 truncate" title={label}>
        {label}
      </div>
      {skuId ? (
        <div className="text-xs text-slate-500 dark:text-slate-400 font-mono truncate" title={skuId}>
          {skuId}
        </div>
      ) : null}
    </div>
  );
}

function unmatchedCell(row) {
  if (!row.in_unmatched_feed || !row.unmatched) {
    return <span className="text-slate-400">—</span>;
  }
  const { title, store_count: storeCount, status, draft_product_id: draftId, priority } = row.unmatched;
  return (
    <div className="min-w-0">
      <div className="text-slate-800 dark:text-slate-100 truncate" title={title || undefined}>
        {title || "—"}
      </div>
      <div className="text-xs text-slate-500 dark:text-slate-400 flex flex-wrap gap-x-2 gap-y-0.5">
        {storeCount > 0 ? <span>{storeCount.toLocaleString()} stores</span> : null}
        {status ? <span>{status}</span> : null}
        {draftId ? <span>Draft linked</span> : null}
        {priority ? <span className="text-amber-600 dark:text-amber-400">Priority</span> : null}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, active, onClick, tone = "slate" }) {
  const toneClass =
    tone === "green"
      ? "border-emerald-200 bg-emerald-50 dark:border-emerald-900/50 dark:bg-emerald-950/30"
      : tone === "amber"
        ? "border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30"
        : tone === "red"
          ? "border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-950/30"
          : "border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/40";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-lg border px-3 py-2.5 text-left transition-shadow ${
        active ? "ring-2 ring-theme-button-primary ring-offset-1 dark:ring-offset-slate-900" : "hover:shadow-sm"
      } ${toneClass}`}
    >
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </div>
      <div className="text-xl font-semibold text-slate-800 dark:text-slate-100 tabular-nums">
        {value.toLocaleString()}
      </div>
    </button>
  );
}

const DraftProductsUpcCsvModal = ({ open, onClose }) => {
  const [step, setStep] = useState(1);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRowCount, setCsvRowCount] = useState(0);
  const [dataRows, setDataRows] = useState([]);
  const [firstDataRowCells, setFirstDataRowCells] = useState([]);
  const [fieldMapping, setFieldMapping] = useState({});
  const [mappingError, setMappingError] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [resultFilter, setResultFilter] = useState(RESULT_FILTER_ALL);

  const reset = useCallback(() => {
    setStep(1);
    setUploadedFiles([]);
    setCsvHeaders([]);
    setCsvRowCount(0);
    setDataRows([]);
    setFirstDataRowCells([]);
    setFieldMapping({});
    setMappingError("");
    setLookupLoading(false);
    setLookupError("");
    setLookupResult(null);
    setResultFilter(RESULT_FILTER_ALL);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const handleFileChange = useCallback((files) => {
    setUploadedFiles(files);
    setMappingError("");
    setLookupError("");
    if (files?.[0]) {
      Papa.parse(files[0], {
        complete(results) {
          const headers = (results.data[0] || []).map((h) => String(h ?? "").trim());
          setCsvHeaders(headers);

          const rows = results.data
            .slice(1)
            .filter(
              (row) =>
                Array.isArray(row) &&
                row.some(
                  (cell) =>
                    cell !== undefined && cell !== null && String(cell).trim() !== ""
                )
            );
          setDataRows(rows);
          setCsvRowCount(rows.length);

          const firstRow = rows[0] || [];
          setFirstDataRowCells(
            headers.map((_, i) => {
              const cell = firstRow[i];
              if (cell === undefined || cell === null) return "";
              return String(cell).trim();
            })
          );

          const initialMapping = {};
          const guessed = guessUpcColumn(headers);
          headers.forEach((h) => {
            initialMapping[h] = h === guessed ? UPC_FIELD : "";
          });
          setFieldMapping(initialMapping);
        },
        skipEmptyLines: true,
      });
    } else {
      setCsvHeaders([]);
      setDataRows([]);
      setCsvRowCount(0);
      setFirstDataRowCells([]);
      setFieldMapping({});
    }
  }, []);

  const upcColumn = useMemo(() => {
    return Object.entries(fieldMapping).find(([, v]) => v === UPC_FIELD)?.[0] || "";
  }, [fieldMapping]);

  const handleUpcColumnChange = useCallback((csvHeader) => {
    setFieldMapping((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((csvField) => {
        if (next[csvField] === UPC_FIELD) {
          next[csvField] = "";
        }
      });
      if (csvHeader) {
        next[csvHeader] = UPC_FIELD;
      }
      return next;
    });
  }, []);

  const getExampleForCsvColumn = useCallback(
    (csvHeader) => {
      const colIndex = csvHeaders.indexOf(csvHeader);
      if (colIndex === -1) return "";
      return firstDataRowCells[colIndex] ?? "";
    },
    [csvHeaders, firstDataRowCells]
  );

  const runLookup = useCallback(async () => {
    if (!upcColumn) {
      setMappingError(`Please map one column to ${UPC_FIELD_LABEL}.`);
      return;
    }

    const upcs = extractUpcsFromRows(csvHeaders, dataRows, upcColumn);
    if (!upcs.length) {
      setMappingError("No UPC values found in the mapped column.");
      return;
    }

    setMappingError("");
    setLookupError("");
    setStep(3);
    setLookupLoading(true);
    setLookupResult(null);

    try {
      const mergedResults = [];
      const mergedSummary = {
        total_inputs: 0,
        unique_upcs: 0,
        invalid: 0,
        in_catalog: 0,
        in_unmatched_feed: 0,
        not_found: 0,
        queued: 0,
        reprioritized: 0,
        skipped_for_queue: 0,
      };

      for (let i = 0; i < upcs.length; i += MAX_LOOKUP_BATCH) {
        const chunk = upcs.slice(i, i + MAX_LOOKUP_BATCH);
        const res = await lookupDraftMasterMenuUPCs(chunk, { queueForAutocreate: true });
        const data = res?.data;
        if (data?.results?.length) {
          mergedResults.push(...data.results);
        }
        if (data?.summary) {
          mergedSummary.total_inputs += data.summary.total_inputs || 0;
          mergedSummary.unique_upcs += data.summary.unique_upcs || 0;
          mergedSummary.invalid += data.summary.invalid || 0;
          mergedSummary.in_catalog += data.summary.in_catalog || 0;
          mergedSummary.in_unmatched_feed += data.summary.in_unmatched_feed || 0;
          mergedSummary.not_found += data.summary.not_found || 0;
          mergedSummary.queued += data.summary.queued || 0;
          mergedSummary.reprioritized += data.summary.reprioritized || 0;
          mergedSummary.skipped_for_queue += data.summary.skipped_for_queue || 0;
        }
      }

      setLookupResult({ results: mergedResults, summary: mergedSummary });
    } catch (error) {
      setLookupError(
        error?.response?.data?.message || error.message || "UPC lookup failed"
      );
    } finally {
      setLookupLoading(false);
    }
  }, [csvHeaders, dataRows, upcColumn]);

  const filteredResults = useMemo(() => {
    const rows = lookupResult?.results || [];
    if (resultFilter === RESULT_FILTER_ALL) return rows;
    return rows.filter((row) => rowMatchesFilter(row, resultFilter));
  }, [lookupResult, resultFilter]);

  const summary = lookupResult?.summary;

  return (
    <ModalBasic
      id="draft-products-upc-csv-modal"
      modalOpen={open}
      setModalOpen={(v) => {
        if (!v) handleClose();
      }}
      title="Upload UPC CSV"
      width="xl"
    >
      {step === 1 ? (
        <div className="flex flex-col gap-6">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Upload a CSV of UPC codes to check whether each barcode already exists in the Saucey
            catalog or unmatched inventory feed. UPCs that are not in the catalog are automatically
            queued for the autocreate worker (priority) to generate draft products.
          </p>
          <FileUpload
            accept={[".csv"]}
            multiple={false}
            inputContent="Drag & drop or click to upload a UPC CSV file"
            onChange={handleFileChange}
          />
          <div className="flex justify-end gap-2">
            <SecondaryButton onClick={handleClose}>Cancel</SecondaryButton>
            <PrimaryButton onClick={() => setStep(2)} disabled={uploadedFiles.length === 0}>
              Next
            </PrimaryButton>
          </div>
        </div>
      ) : null}

      {step === 2 ? (
        <div className="flex flex-col gap-6">
          <div>
            <h3 className="font-semibold mb-2">Map UPC field to CSV column</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
              Choose which CSV column contains UPC or barcode values.
              {csvRowCount > 0 ? (
                <span className="block mt-1">
                  {csvRowCount.toLocaleString()} data {csvRowCount === 1 ? "row" : "rows"}{" "}
                  detected.
                  {firstDataRowCells.some((c) => c !== "") ? (
                    <>
                      {" "}
                      Example values are from the{" "}
                      <span className="font-medium text-slate-600 dark:text-slate-300">
                        first data row
                      </span>{" "}
                      of your file (for reference only).
                    </>
                  ) : null}
                </span>
              ) : (
                <span className="block mt-1 text-amber-600 dark:text-amber-400">
                  No data rows found after the header; examples will not be shown.
                </span>
              )}
            </p>
            {mappingError ? (
              <div className="mb-2 text-red-500 font-medium">{mappingError}</div>
            ) : null}
            <div className="space-y-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-4">
                <div className="sm:w-1/3 min-w-0 shrink-0">
                  <div className="text-sm text-slate-700 dark:text-slate-200">
                    <span className="text-red-500 mr-1">*</span>
                    {UPC_FIELD_LABEL}
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    Required
                  </div>
                </div>
                <div className="w-full sm:w-2/3 sm:flex-1 min-w-0">
                  <select
                    className={`form-select w-full ${mappingError && !upcColumn ? "border-red-500" : ""}`}
                    value={upcColumn}
                    onChange={(e) => handleUpcColumnChange(e.target.value)}
                  >
                    <option value="">— Select CSV column —</option>
                    {csvHeaders.map((csvField, colIndex) => {
                      const example = firstDataRowCells[colIndex] ?? "";
                      const optionLabel = example
                        ? `${csvField || `(column ${colIndex + 1})`} — e.g. ${formatMappingExample(example)}`
                        : csvField || `(column ${colIndex + 1})`;

                      return (
                        <option key={`${csvField}-${colIndex}`} value={csvField}>
                          {optionLabel}
                        </option>
                      );
                    })}
                  </select>
                  {upcColumn ? (
                    <div
                      className="mt-1.5 text-xs text-slate-500 dark:text-slate-400"
                      title={getExampleForCsvColumn(upcColumn) || upcColumn}
                    >
                      <span className="font-medium text-slate-500 dark:text-slate-500">
                        Column:
                      </span>{" "}
                      <span className="font-mono text-slate-600 dark:text-slate-300">
                        {upcColumn}
                      </span>
                      {getExampleForCsvColumn(upcColumn) !== "" ? (
                        <>
                          {" "}
                          <span className="text-slate-400">·</span>{" "}
                          <span className="font-medium text-slate-500 dark:text-slate-500">
                            Example:
                          </span>{" "}
                          <span className="text-slate-600 dark:text-slate-300 break-all line-clamp-2">
                            {getExampleForCsvColumn(upcColumn)}
                          </span>
                        </>
                      ) : (
                        <span className="ml-1 italic text-slate-400 dark:text-slate-500">
                          (no value in first row)
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="mt-1.5 text-xs text-slate-400 dark:text-slate-500 italic">
                      No CSV column selected
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-between gap-2">
            <SecondaryButton onClick={() => setStep(1)}>Back</SecondaryButton>
            <div className="flex gap-2">
              <SecondaryButton onClick={handleClose}>Cancel</SecondaryButton>
              <PrimaryButton onClick={runLookup} disabled={!upcColumn || csvRowCount === 0}>
                {csvRowCount > 0
                  ? `Check & queue ${csvRowCount.toLocaleString()} UPCs`
                  : "Check & queue UPCs"}
              </PrimaryButton>
            </div>
          </div>
        </div>
      ) : null}

      {step === 3 ? (
        <div className="flex flex-col gap-5">
          {lookupLoading ? (
            <div className="flex flex-col items-center justify-center min-h-[220px] gap-3">
              <Loader />
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Cross-checking against catalog and unmatched feed…
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Eligible UPCs will be added to the autocreate queue when the check completes.
              </p>
            </div>
          ) : lookupError ? (
            <div className="space-y-4">
              <div className="rounded border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 px-4 py-3 text-sm text-red-800 dark:text-red-200">
                {lookupError}
              </div>
              <div className="flex justify-end gap-2">
                <SecondaryButton onClick={() => setStep(2)}>Back</SecondaryButton>
                <PrimaryButton onClick={handleClose}>Close</PrimaryButton>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                <SummaryCard
                  label="Unique UPCs"
                  value={summary?.unique_upcs ?? 0}
                  active={resultFilter === RESULT_FILTER_ALL}
                  onClick={() => setResultFilter(RESULT_FILTER_ALL)}
                />
                <SummaryCard
                  label="Queued"
                  value={(summary?.queued ?? 0) + (summary?.reprioritized ?? 0)}
                  tone="green"
                  active={resultFilter === RESULT_FILTER_QUEUED}
                  onClick={() => setResultFilter(RESULT_FILTER_QUEUED)}
                />
                <SummaryCard
                  label="In catalog"
                  value={summary?.in_catalog ?? 0}
                  tone="green"
                  active={resultFilter === RESULT_FILTER_CATALOG}
                  onClick={() => setResultFilter(RESULT_FILTER_CATALOG)}
                />
                <SummaryCard
                  label="Already on feed"
                  value={Math.max(
                    0,
                    (summary?.in_unmatched_feed ?? 0) -
                      (summary?.queued ?? 0) -
                      (summary?.reprioritized ?? 0)
                  )}
                  tone="amber"
                  active={resultFilter === RESULT_FILTER_UNMATCHED}
                  onClick={() => setResultFilter(RESULT_FILTER_UNMATCHED)}
                />
                <SummaryCard
                  label="Skipped"
                  value={summary?.skipped_for_queue ?? 0}
                  active={false}
                  onClick={() => setResultFilter(RESULT_FILTER_ALL)}
                />
                <SummaryCard
                  label="Invalid"
                  value={summary?.invalid ?? 0}
                  tone="red"
                  active={resultFilter === RESULT_FILTER_INVALID}
                  onClick={() => setResultFilter(RESULT_FILTER_INVALID)}
                />
                <SummaryCard
                  label="CSV rows"
                  value={summary?.total_inputs ?? 0}
                  active={false}
                  onClick={() => setResultFilter(RESULT_FILTER_ALL)}
                />
              </div>

              {(summary?.queued ?? 0) + (summary?.reprioritized ?? 0) > 0 ? (
                <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-900 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-100">
                  <span className="font-semibold">
                    {((summary?.queued ?? 0) + (summary?.reprioritized ?? 0)).toLocaleString()}
                  </span>{" "}
                  UPC
                  {(summary?.queued ?? 0) + (summary?.reprioritized ?? 0) === 1 ? "" : "s"}{" "}
                  {summary?.queued && summary?.reprioritized
                    ? "queued or reprioritized"
                    : summary?.reprioritized
                      ? "reprioritized"
                      : "queued"}{" "}
                  for autocreate. Draft products will appear on this page as the worker processes
                  them.
                </div>
              ) : null}

              <p className="text-xs text-slate-500 dark:text-slate-400">
                Showing {filteredResults.length.toLocaleString()} of{" "}
                {(lookupResult?.results?.length ?? 0).toLocaleString()} unique UPCs.
                {summary?.total_inputs > summary?.unique_upcs ? (
                  <>
                    {" "}
                    {summary.total_inputs - summary.unique_upcs} duplicate row
                    {summary.total_inputs - summary.unique_upcs === 1 ? "" : "s"} in the CSV were
                    collapsed for lookup.
                  </>
                ) : null}
              </p>

              <div className="overflow-x-auto rounded border border-slate-200 dark:border-slate-700 max-h-[420px] overflow-y-auto">
                <table className="w-full table-auto text-left text-sm">
                  <thead className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-800/95 text-slate-600 dark:text-slate-300">
                    <tr>
                      <th className="px-3 py-2 font-semibold">UPC</th>
                      <th className="px-3 py-2 font-semibold w-40">Status</th>
                      <th className="px-3 py-2 font-semibold min-w-[160px]">Catalog product</th>
                      <th className="px-3 py-2 font-semibold min-w-[160px]">Unmatched feed</th>
                      <th className="px-3 py-2 font-semibold min-w-[140px]">Queue</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {filteredResults.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-3 py-8 text-center text-slate-500">
                          No rows in this filter.
                        </td>
                      </tr>
                    ) : (
                      filteredResults.map((row) => (
                        <tr
                          key={row.input_upc}
                          className="hover:bg-slate-50 dark:hover:bg-slate-800/40"
                        >
                          <td className="px-3 py-2 align-top">
                            <div className="font-mono text-slate-800 dark:text-slate-100">
                              {row.input_upc}
                            </div>
                            {row.canonical_upc && row.canonical_upc !== row.input_upc ? (
                              <div
                                className="text-xs text-slate-500 font-mono truncate"
                                title={row.canonical_upc}
                              >
                                → {row.canonical_upc}
                              </div>
                            ) : null}
                          </td>
                          <td className="px-3 py-2 align-top">{statusBadge(row)}</td>
                          <td className="px-3 py-2 align-top">{catalogCell(row)}</td>
                          <td className="px-3 py-2 align-top">{unmatchedCell(row)}</td>
                          <td className="px-3 py-2 align-top text-sm text-slate-600 dark:text-slate-300">
                            {row.queue_note || "—"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-between gap-2">
                <SecondaryButton onClick={reset}>Upload another file</SecondaryButton>
                <PrimaryButton onClick={handleClose}>Done</PrimaryButton>
              </div>
            </>
          )}
        </div>
      ) : null}
    </ModalBasic>
  );
};

export default DraftProductsUpcCsvModal;
